"use client";

import React, { useEffect, useState, useCallback, useMemo } from "react";
import Link from "next/link";
import { MenuIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { getCalApi } from "@calcom/embed-react";

type Props = object;

const links = [
  { label: "Features", href: "#features" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pasoma CLI", href: "#cli" },
  { label: "Community", href: "#footer" },
];

function Navbar({}: Props) {
  const [isLightSection, setIsLightSection] = useState(false);

  // Throttled scroll handler for better performance
  const handleScroll = useCallback(() => {
    try {
      const cliSection = document.getElementById("cli");
      if (!cliSection) return;

      const rect = cliSection.getBoundingClientRect();
      const isInCliSection = rect.top <= 100 && rect.bottom >= 100;
      setIsLightSection((prev) =>
        prev !== isInCliSection ? isInCliSection : prev
      );
    } catch (error) {
      console.warn("Error checking CLI section position:", error);
    }
  }, []);

  // Throttle scroll events for better performance
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const throttledHandleScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null as any;
      }, 16); // ~60fps
    };

    // Check initial position
    handleScroll();

    // Add scroll listener
    window.addEventListener("scroll", throttledHandleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  // Initialize Cal.com embed
  useEffect(() => {
    (async function () {
      try {
        const cal = await getCalApi({ namespace: "call-with-pasoma-team" });
        cal("ui", { hideEventTypeDetails: true, layout: "month_view" });
      } catch (error) {
        console.warn("Failed to initialize Cal.com embed:", error);
      }
    })();
  }, []);

  // Memoize className calculations for better performance
  const navbarClasses = useMemo(
    () => ({
      container: `relative overflow-hidden rounded-full px-3 py-2 backdrop-blur-xl transition-all duration-300 ${
        isLightSection
          ? "border border-gray-200/50 bg-gradient-to-r from-white/80 to-white/90 shadow-lg"
          : "border border-white/10 bg-gradient-to-r from-white/5 to-white/10"
      }`,
      overlay: `absolute inset-0 rounded-full transition-all duration-300 ${
        isLightSection
          ? "bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-30"
          : "bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-50"
      }`,
      logo: `text-lg font-bold ${
        isLightSection
          ? "bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent"
          : "bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent"
      }`,
      navLink: `text-sm transition-all duration-200 ${
        isLightSection
          ? "text-gray-700 hover:text-gray-900"
          : "text-zinc-300 hover:text-white"
      }`,
      button: `group relative hidden overflow-hidden rounded-full shadow-lg transition-all duration-300 md:inline-flex ${
        isLightSection
          ? "bg-gradient-to-r from-gray-800 to-gray-900 text-white hover:from-gray-700 hover:to-gray-800 hover:shadow-xl"
          : "bg-gradient-to-r from-zinc-700 to-zinc-800 text-white hover:from-zinc-600 hover:to-zinc-700 hover:shadow-xl"
      }`,
      mobileButton: `rounded-full transition-all duration-200 md:hidden ${
        isLightSection
          ? "border-gray-400 bg-transparent text-gray-700 hover:border-gray-500 hover:bg-gray-100/50 hover:text-gray-900"
          : "border-zinc-600 bg-transparent text-zinc-300 hover:border-zinc-400 hover:bg-zinc-800/50 hover:text-white"
      }`,
      mobileMenuButton: `mt-2 self-start rounded-full shadow-lg transition-all duration-300 ${
        isLightSection
          ? "bg-gradient-to-r from-gray-800 to-gray-900 text-white hover:from-gray-700 hover:to-gray-800 hover:shadow-xl"
          : "bg-gradient-to-r from-zinc-700 to-zinc-800 text-white hover:from-zinc-600 hover:to-zinc-700 hover:shadow-xl"
      }`,
      mobileMenuLink: `text-foreground/90 hover:text-foreground rounded-md px-1 py-2 text-base ${
        isLightSection
          ? "text-gray-700 hover:text-gray-900"
          : "text-foreground/90 hover:text-foreground"
      }`,
    }),
    [isLightSection]
  );

  return (
    <header className="sticky top-4 z-50 mx-auto w-full max-w-6xl px-3">
      <div className={navbarClasses.container}>
        {/* Subtle background gradient overlay */}
        <div className={navbarClasses.overlay} />

        <div className="relative flex items-center justify-between">
          <Link href="/" className="pl-3 font-semibold tracking-tight">
            <span className={navbarClasses.logo}>PASOMA</span>
          </Link>

          <nav className="hidden items-center gap-6 md:flex">
            {links.map((l) => (
              <Link
                key={l.label}
                href={l.href}
                className={navbarClasses.navLink}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              className={navbarClasses.button}
              data-cal-namespace="call-with-pasoma-team"
              data-cal-link="faruk-pasoma/call-with-pasoma-team"
              data-cal-config='{"layout":"month_view"}'
            >
              <span className="relative z-10">Book a Demo</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={navbarClasses.mobileButton}
                >
                  <MenuIcon className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[80%] max-w-sm">
                <SheetHeader>
                  <SheetTitle className="text-left"> </SheetTitle>
                </SheetHeader>
                <div className="mt-2 flex flex-col gap-3 p-5">
                  {links.map((l) => (
                    <SheetClose asChild key={l.label}>
                      <Link
                        href={l.href}
                        className={navbarClasses.mobileMenuLink}
                      >
                        {l.label}
                      </Link>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Button
                      className={navbarClasses.mobileMenuButton}
                      data-cal-namespace="call-with-pasoma-team"
                      data-cal-link="faruk-pasoma/call-with-pasoma-team"
                      data-cal-config='{"layout":"month_view"}'
                    >
                      Book a Demo
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
